import React from 'react';
import { PiTrashDuotone } from 'react-icons/pi';
import { Button, Input } from 'rizzui';
import CustomSelect from '../../../components/forms/CustomSelect';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { FilterOptions } from '../../../hooks/useFilters';
import { RootState } from '../../../redux/store';
import { useSelector } from 'react-redux';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import useParks from '../../../hooks/api-calls/useParks';
import useWindTurbine from '../../../hooks/api-calls/useWindTurbine';
import useUsers from '../../../hooks/api-calls/useUsers';
import useSpecies from '../../../hooks/api-calls/useSpecies';
import useClients from '../../../hooks/api-calls/useClients';
import { cloudCover, windDirectionsSpanish, windSpeed } from '../../../utils/SelectorMapping';

interface CarrionFiltersProps {
    updateFilters: (filters: any) => void
    resetFilters: (limit: any) => void
    filters: FilterOptions
}

const CarrionFilters: React.FC<CarrionFiltersProps> = ({ filters, updateFilters, resetFilters }) => {

    // HOOKS

    const { userCan } = usePrivilege();
    const { getCompaniesList } = useCompanies();
    const { getParksList } = useParks();
    const { getWindTurbinesList } = useWindTurbine();
    const { getSpeciesList, getSpeciesListScientific } = useSpecies({ carrionAvailable: true });
    const { getClientsList } = useClients();
    const { getUsersList } = useUsers();
    const { user } = useSelector((state: RootState) => state.auth);
    const isSuperAdmin = user?.roles.includes('Superadministrador');

    // RENDER

    return (
        <>

            <CustomSelect
                isSearchable
                id={'client'}
                label="Cliente"
                options={getClientsList()}
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ client: null });
                        return;
                    }
                    updateFilters({ client: e.value });
                }}
                value={filters.filter_filters?.client
                    ? getClientsList().filter((client: any) => filters.filter_filters?.client.includes(client.value))
                    : null
                }
                display={userCan('admin_client', 'clients')}
            />

            <CustomSelect
                isSearchable
                id={'park'}
                label="Parque eólico"
                options={ getParksList() }
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ park: null });
                        return;
                    }
                    updateFilters({ park: e.value });
                }}
                value={filters.filter_filters?.park
                    ? getParksList().filter((park: any) => filters.filter_filters?.park.includes(park.value))
                    : null
                }
                display={userCan('admin_park', 'park')}
            />

            <CustomSelect
                isSearchable
                id={'wind_turbine'}
                label="Aerogenerador"
                options={ getWindTurbinesList() }
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ windturbine: null });
                        return;
                    }
                    updateFilters({ windturbine: e.value });
                }}
                value={filters.filter_filters?.windturbine
                    ? getWindTurbinesList().filter((turbine: any) => filters.filter_filters?.windturbine.includes(turbine.value))
                    : null
                }
                display={userCan('admin_windturbine', 'windturbine')}
            />

            <CustomSelect
                isSearchable
                id={'species'}
                italic
                label="Especie de la carroña"
                options={ getSpeciesListScientific() }
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ species: null });
                        return;
                    }
                    updateFilters({ species: e.value });
                }}
                value={filters.filter_filters?.species
                    ? getSpeciesListScientific().filter((species: any) => filters.filter_filters?.species.includes(species.value))
                    : null
                }
                display={userCan('admin_species', 'species')}
            />

            <CustomSelect
                isSearchable
                id={'avianPresencia'}
                label="Presencia de Aves"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ avianPresencia: null });
                        return;
                    }
                    updateFilters({ avianPresencia: e.value });
                }}
                value={filters.filter_filters?.avianPresencia !== null && filters.filter_filters?.avianPresencia !== undefined
                    ? { label: filters.filter_filters?.avianPresencia ? 'Sí' : 'No', value: filters.filter_filters?.avianPresencia }
                    : { label: 'Todos', value: 'null' }}
            />

            <div className="flex flex-col gap-2">
                <span className="text-sm font-semibold text-gray-500">Distancia desde el aerogenerador (m)</span>
                <div className="flex gap-2">
                    <Input
                        id="distanceFromTurbineMin"
                        placeholder="Desde"
                        type="number"
                        value={filters.filter_filters?.distances?.from ?? ''}
                        onChange={(e: any) => updateFilters({ 
                            distances: { 
                                from: e.target.value, 
                                to: filters.filter_filters?.distances?.to ? filters.filter_filters.distances.to : null
                            } 
                        })}
                    />
                    <Input
                        id="distanceFromTurbineMax"
                        placeholder="Hasta"
                        type="number"
                        value={filters.filter_filters?.distances?.to ?? ''}
                        onChange={(e: any) => updateFilters({ 
                            distances: { 
                                from: filters.filter_filters?.distances?.from ? filters.filter_filters.distances.from : null, 
                                to: e.target.value 
                            } 
                        })}
                    />
                </div>
            </div>

            <CustomSelect
                isSearchable
                id={'windDirection'}
                label="Dirección del viento"
                options={windDirectionsSpanish}
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ windDirection: null });
                        return;
                    }
                    updateFilters({ windDirection: e.value });
                }}
                value={filters.filter_filters?.windDirection
                    ? windDirectionsSpanish.filter((windDirection: any) => filters.filter_filters?.windDirection.includes(windDirection.value))
                    : null
                }
            />

            <CustomSelect
                isSearchable
                id={'windSpeed'}
                label="Intensidad del viento"
                options={windSpeed}
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ windSpeed: null });
                        return;
                    }
                    updateFilters({ windSpeed: e.value });
                }}
                value={filters.filter_filters?.windSpeed
                    ? windSpeed.filter((windSpeed: any) => filters.filter_filters?.windSpeed.includes(windSpeed.value))
                    : null
                }
            />

            <CustomSelect
                isSearchable
                id={'cloudCoverage'}
                label="Cobertura de nubes"
                options={cloudCover}
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ cloudCoverage: null });
                        return;
                    }
                    updateFilters({ cloudCoverage: e.value });
                }}
                value={filters.filter_filters?.cloudCoverage
                    ? cloudCover.filter((cloudCoverage: any) => filters.filter_filters?.cloudCoverage.includes(cloudCoverage.value))
                    : null
                }
            />

            <Input
                id="age"
                label="Edad del ejemplar"
                placeholder="Buscar por edad del ejemplar"
                value={filters.filter_filters?.age ?? ''}
                onChange={(e: any) => updateFilters({ age: e.target.value })}
            />

            <CustomSelect
                isSearchable
                id={'notifiedEnvAgency'}
                label="Aviso a Medio Ambiente (AMA)"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ notifiedEnvAgency: null });
                        return;
                    }
                    updateFilters({ notifiedEnvAgency: e.value });
                }}
                value={filters.filter_filters?.notifiedEnvAgency !== null && filters.filter_filters?.notifiedEnvAgency !== undefined
                    ? { label: filters.filter_filters?.notifiedEnvAgency ? 'Sí' : 'No', value: filters.filter_filters?.notifiedEnvAgency }
                    : { label: 'Todos', value: 'null' }}
            />

            <CustomSelect
                isSearchable
                id={'notifiedOwner'}
                label="Notificacion al propietario"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ notifiedOwner: null });
                        return;
                    }
                    updateFilters({ notifiedOwner: e.value });
                }}
                value={filters.filter_filters?.notifiedOwner !== null && filters.filter_filters?.notifiedOwner !== undefined
                    ? { label: filters.filter_filters?.notifiedOwner ? 'Sí' : 'No', value: filters.filter_filters?.notifiedOwner }
                    : { label: 'Todos', value: 'null' }}
            />

            <CustomSelect
                isSearchable
                id={'user'}
                label="Observador"
                options={ getUsersList() }
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ user: null });
                        return;
                    }
                    updateFilters({ user: e.value });
                }}
                value={filters.filter_filters?.user
                    ? getUsersList().filter((user: any) => filters.filter_filters?.user.includes(user.value))
                    : null
                }
                display={userCan('admin_user', 'user')}

            />

            {isSuperAdmin && (
                <CustomSelect
                    isSearchable
                    id={'company'}
                    label="Compañia"
                    options={getCompaniesList()}
                    onChange={(e: any) => { 
                        updateFilters({ company: e.value})
                    }}
                    value={
                        filters.filter_filters?.company
                            ? getCompaniesList().filter((company: any) => filters.filter_filters?.company.includes(company.value))
                            : null
                            
                    }
                    display={userCan('list', 'companies')}
                />
            )}

            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>
        </>
    );
};

export default CarrionFilters;